import {useRef, useEffect, useState} from "react"
import {Editor} from "@tinymce/tinymce-react"
import useEcpTranscription from "../../swrCustomHooks/useEcpTranscription"
import {useTranslation} from "react-i18next"
import LoadingComponent from "../../components/loading-component/LoadingComponent"
import axios from "axios"
import {API_ECP_PROJECTS} from "../../constants/apiUrls"
import {toast} from "react-toastify"
import {useSessionContext} from "../../reactContext/contextCustomHooks/useSessionContext"
import {useTeamContext} from "../../reactContext/contextCustomHooks/useTeamContext"
import {useEcpWorkflowContext} from "../../reactContext/contextCustomHooks/useEcpWorkflowContext"
import isLicenceValidity from "../../tools/isLicenceValidity"

export default function EcpWorkflowTranscription({project}) {
    const {t} = useTranslation()
    const {sessionContext} = useSessionContext()
    const {teamContext} = useTeamContext()
    const {setEcpWorkflowAction} = useEcpWorkflowContext()
    const {ecpTranscription, isLoadingEcpTranscription, isErrorEcpTranscription} = useEcpTranscription(project.id)
    const [dirty, setDirty] = useState(false)
    const editorRef = useRef(null)
    const headersRequest = {headers: {Authorization: `Bearer ${sessionContext.idToken}`}}

    const saveEditorContent = () => {
        return new Promise((resolve, reject) => {
            if (editorRef.current) {
                const content = editorRef.current.getContent()
                setDirty(false)
                editorRef.current.setDirty(false)
                axios.put(`${API_ECP_PROJECTS}/${project.id}/transcription`, content, headersRequest)
                .then(() => {
                    toast.success(t("Your transcription is saved"))
                    resolve()
                })
                .catch((error) => {
                    console.error(`PUT /projects/${project.id}/transcription:`, error)
                    toast.error(t("An error has occurred"))
                    reject(error)
                })
            }
        })
    }

    const handleClick = () => {
        if (isLicenceValidity(teamContext.licenceStartDate, teamContext.licenceEndDate)) {
            const chars = editorRef.current.plugins.wordcount.body.getCharacterCountWithoutSpaces()
            if (chars >= 500) {
                saveEditorContent().then(() => {
                    setEcpWorkflowAction({type: "ECP_WORKFLOW_IS_CONFIRMATION_MODAL", payload: {isConfirmationModal: {modal: true, id: project.id, type: "ecp-generate"}}})
                })
            } else {
                toast.error(`${t("The transcript is less than 500 characters.")} (${chars} ${t("without space")})`)
            }
        } else {
            toast.error(t("Licence expired. Please contact Neurowide sales department."))
        }
    }

    useEffect(() => {
        setDirty(false)
    }, [])

    if (isErrorEcpTranscription || isLoadingEcpTranscription || ecpTranscription?.message) return <LoadingComponent/>
    return (
        <>
        <div className="SECTION ECP-WORKFLOW-TRANSCRIPT">
            <Editor
                tinymceScriptSrc={"/tinymce/tinymce.min.js"}
                initialValue={ecpTranscription}
                onInit={(evt, editor) => editorRef.current = editor}
                onDirty={() => setDirty(true)}
                init={{
                license_key: "gpl",
                height: 500,
                menubar: false,
                plugins: [
                    "charmap", "searchreplace", "wordcount"
                ],
                paste_data_images: false,
                toolbar: "undo redo | bold italic | removeformat | searchreplace | save",
                content_style: "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
                setup: (editor) => {
                    editor.ui.registry.addIcon("custom-save", `<svg class="custom-save" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#C30F0E"><path d="M840-680v480q0 33-23.5 56.5T760-120H200q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h480l160 160Zm-80 34L646-760H200v560h560v-446ZM480-240q50 0 85-35t35-85q0-50-35-85t-85-35q-50 0-85 35t-35 85q0 50 35 85t85 35ZM240-560h360v-160H240v160Zm-40-86v446-560 114Z"/></svg>`)
                    !project.finished && editor.ui.registry.addButton("save", {
                        icon: "custom-save",
                        tooltip: t("Save"),
                        onAction: () => {
                            if (isLicenceValidity(teamContext.licenceStartDate, teamContext.licenceEndDate)) {
                                saveEditorContent()
                            } else {
                                toast.error(t("Licence expired. Please contact Neurowide sales department."))
                            }
                        }
                    })
                }
                }}
            />
            {(!project.finished && project.generate_all_ecp_status !== "done") &&
                <div className="actions">
                    <button onClick={handleClick} title={t("Generate")}>{t("Generate")}</button>
                </div>
            }
        </div>
        </>
    )
}